import { captureAppError } from '@leagueplatform/observability';
import { SENTRY_CONSTANTS } from 'common/constants';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@leagueplatform/auth-utils';
import { StandaloneAuth } from '@leagueplatform/auth-standalone';
import { useLocation, useNavigate } from 'react-router-dom';
import { endAgentSession } from 'utils/agent-session';
import {
  getAuthConnection,
  hasExplicitAuthSelector,
  isDevelopmentAuthEnvironment,
} from 'utils/auth-connection';
import { getAuthRedirectPath } from 'utils/auth-redirect-path';

export const useLandingPage = () => {
  // Complete the SDK callback/session check before starting another transaction.
  const { isAuthenticated, error, appState } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const callbackParams = new URLSearchParams(location.search);
  const enteredWithCallback = useRef(
    callbackParams.has('state') &&
      (callbackParams.has('code') || callbackParams.has('error')),
  );
  // The pinned SDK can resolve a failed callback to a signed-out state without
  // an error. Do not turn that result into an automatic login loop.
  const callbackFailed =
    enteredWithCallback.current && isAuthenticated === false;
  const startedRedirect = useRef(false);
  const [redirectFailed, setRedirectFailed] = useState(false);

  // Keep the development-only choice visible until a member/developer link is
  // chosen. Explicit connection URLs and callbacks retain their direct flow.
  const showDevLoginOptions =
    isDevelopmentAuthEnvironment() &&
    isAuthenticated === false &&
    !error &&
    !redirectFailed &&
    !enteredWithCallback.current &&
    !hasExplicitAuthSelector(location.search);

  useEffect(() => {
    if (
      error ||
      redirectFailed ||
      callbackFailed ||
      showDevLoginOptions ||
      isAuthenticated === undefined
    )
      return;

    if (isAuthenticated) {
      navigate(getAuthRedirectPath(appState ?? location.state), {
        replace: true,
      });
      return;
    }

    // React StrictMode replays effects; a second transaction loses callback state.
    if (startedRedirect.current) return;
    startedRedirect.current = true;
    // Only a fresh member login clears an old agent marker. A successful
    // support callback above must retain it for timeout and sign-out behavior.
    endAgentSession();

    StandaloneAuth.client
      .loginWithRedirect({
        connection: getAuthConnection(location.search),
        appState: {
          redirectPath: getAuthRedirectPath(location.state),
          invalidateSession: true,
        },
      })
      .catch(() => {
        // Provider errors can contain account details or authorization URLs.
        // Report the failed operation without forwarding the raw rejection.
        captureAppError(new Error('Member sign-in redirect failed'), {
          ownershipTeam: SENTRY_CONSTANTS.OWNERSHIP_TEAM,
          errorName: 'Member Sign-In Redirect Failed',
        });
        setRedirectFailed(true);
      });
  }, [
    isAuthenticated,
    error,
    appState,
    location.state,
    location.search,
    navigate,
    redirectFailed,
    callbackFailed,
    showDevLoginOptions,
  ]);

  return {
    hasError: Boolean(error) || redirectFailed || callbackFailed,
    showDevLoginOptions,
  };
};
